import { getUnstyledAllocationVariantLabel } from '@/components/AllocationVariantIndex/getFullAllocationVariantIndex';
import {
    AllocationForDaysRowsFragment,
    ExternalEventLocationVisibility,
    ExternalEventSeriesCategory,
    GetExternalEventAllocationsForDaysRowsDocument,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useQuery } from '@vue/apollo-composable';
import { DateTime } from 'luxon';
import { computed, Ref, toValue } from 'vue';
import {
    EVENT_CATEGORIES,
    getExternalEventVisibilityVariant,
    internationalToolTipHeaderMap,
    mapExternalEventAllocationForTimeline,
    nationalToolTipHeaderMap,
} from '../externalEvent';

export function useExternalEventAggregationRows(
    start: Ref<DateTime | undefined>,
    end: Ref<DateTime | undefined>,
    executionPhasePosition: number,
    visibleVariantIds: Ref<string[]> | undefined,
    overrideVisibilityFilterVariantIds: Ref<string[]> | undefined,
) {
    const { result } = useQuery(
        GetExternalEventAllocationsForDaysRowsDocument,
        () => ({
            startDateTime: start.value!.toISO(),
            endDateTime: end.value!.toISO(),
        }),
        () => ({
            enabled: start.value !== undefined && end.value !== undefined,
        }),
    );

    const allocations = computed(() => result.value?.allocations?.nodes ?? []);

    const visibleAllocations = computed(() => {
        const visibleIds = toValue(visibleVariantIds) ?? [];
        const overrideIds = toValue(overrideVisibilityFilterVariantIds) ?? [];

        const variantFiltered = visibleIds
            ? allocations.value.filter((a) => visibleIds.includes(a.variant.id))
            : allocations.value;

        return variantFiltered.filter((a) => {
            if (overrideIds.includes(a.variant.id)) {
                return true;
            }
            return a.variant.event.visibilities.nodes
                .filter((n) => n.locationId === a.location.id)
                .some((n) => n.visibility !== ExternalEventLocationVisibility.Invisible);
        });
    });

    const nationalLocations = computed(() => result.value?.nationalLocations?.nodes ?? []);

    function mapForLayoutRows(allocation: AllocationForDaysRowsFragment, locationIds: string[]) {
        const variantLabel = getUnstyledAllocationVariantLabel(allocation.variant.index, allocation.variant.name);
        const tooltipHeadline = `${allocation.variant.event.nameLong} - ${variantLabel}`;
        return {
            ...mapExternalEventAllocationForTimeline(
                {
                    ...allocation.variant,
                    referenceAllocationId: allocation.variant.referenceExternalEventAllocationId ?? null,
                },
                allocation,
                'ExternalLocation',
                executionPhasePosition,
                false,
                allocation.variant.event.series.category as ExternalEventSeriesCategory,
            ),
            id: `reference-${allocation.id}`,
            linkTarget: '',
            federalStates: allocation.variant.event.series.national
                ? nationalLocations.value.map((l) => ({
                      id: l.id,
                      name: l.name,
                      isOccupied: locationIds.includes(l.id),
                  }))
                : [],
            tooltipHeader: allocation.variant.event.series.national
                ? nationalToolTipHeaderMap[allocation.variant.event.series.category]
                : internationalToolTipHeaderMap[allocation.variant.event.series.category],
            tooltipHeadline,
            tooltipType: allocation.variant.event.series.national ? 'National' : 'International',
            variant: getExternalEventVisibilityVariant(
                allocation.variant.event.visibilities.nodes
                    .filter((n) => locationIds.includes(n.locationId))
                    .map((n) => n.visibility),
            ),
        };
    }

    function groupByStartEnd(allocations: AllocationForDaysRowsFragment[]) {
        return allocations.reduce(
            (acc, allocation) => {
                const key = `${allocation.start}|${allocation.end}|${allocation.variant.id}`;
                if (!acc[key]) {
                    acc[key] = { allocation, locationIds: [allocation.location.id] };
                }
                acc[key].locationIds.push(allocation.location.id);
                return acc;
            },
            {} as Record<string, { allocation: AllocationForDaysRowsFragment; locationIds: string[] }>,
        );
    }

    function groupAndMapNationalAllocations(allocations: AllocationForDaysRowsFragment[]) {
        const groupedAllocations = groupByStartEnd(allocations);
        const values = Object.values(groupedAllocations ?? {});
        return values.map(({ allocation, locationIds }) => {
            return mapForLayoutRows(allocation, locationIds);
        });
    }

    const publicHolidaysNational = computed(() => {
        const pubHolidays = visibleAllocations.value.filter(
            (a) => a.variant.event.series.national && a.variant.event.series.category === 'public_holiday',
        );
        return groupAndMapNationalAllocations(pubHolidays);
    });

    const eventsNational = computed(() => {
        const eventAllocations = visibleAllocations.value.filter(
            (a) => a.variant.event.series.national && EVENT_CATEGORIES.includes(a.variant.event.series.category),
        );
        return groupAndMapNationalAllocations(eventAllocations);
    });

    const holidaysNational = computed(() => {
        const holidayAllocations = visibleAllocations.value.filter(
            (a) => a.variant.event.series.national && a.variant.event.series.category === 'holiday',
        );
        return groupAndMapNationalAllocations(holidayAllocations);
    });

    const publicHolidaysAndHolidaysInternational = computed(() => {
        return visibleAllocations.value
            .filter(
                (allocation) =>
                    !allocation.variant.event.series.national &&
                    (allocation.variant.event.series.category === 'public_holiday' ||
                        allocation.variant.event.series.category === 'holiday'),
            )
            .map((allocation) => {
                return mapForLayoutRows(allocation, [allocation.location.id]);
            });
    });

    const eventsInternational = computed(() => {
        return visibleAllocations.value
            .filter(
                (allocation) =>
                    !allocation.variant.event.series.national &&
                    EVENT_CATEGORIES.includes(allocation.variant.event.series.category),
            )
            .map((allocation) => {
                return mapForLayoutRows(allocation, [allocation.location.id]);
            });
    });

    const competitionNationalAndInternational = computed(() => {
        const nationalCompetitionAllocations = visibleAllocations.value.filter(
            (a) => a.variant.event.series.national && a.variant.event.series.category === 'competition',
        );
        const mappedNationalCompetitionAllocations = groupAndMapNationalAllocations(nationalCompetitionAllocations);
        const internationalCompetitionAllocations = visibleAllocations.value
            .filter(
                (allocation) =>
                    !allocation.variant.event.series.national &&
                    allocation.variant.event.series.category === 'competition',
            )
            .map((allocation) => {
                return mapForLayoutRows(allocation, [allocation.location.id]);
            });
        return [...mappedNationalCompetitionAllocations, ...internationalCompetitionAllocations];
    });

    const worldNationalAndInternational = computed(() => {
        const nationalWorldAllocations = visibleAllocations.value.filter(
            (a) => a.variant.event.series.national && a.variant.event.series.category === 'world',
        );
        const mappedNationalWorldAllocations = groupAndMapNationalAllocations(nationalWorldAllocations);
        const internationalWorldAllocations = visibleAllocations.value
            .filter(
                (allocation) =>
                    !allocation.variant.event.series.national && allocation.variant.event.series.category === 'world',
            )
            .map((allocation) => {
                return mapForLayoutRows(allocation, [allocation.location.id]);
            });
        return [...mappedNationalWorldAllocations, ...internationalWorldAllocations];
    });

    const rowData = computed(() => [
        { id: 'public-holidays-national-row', allocations: publicHolidaysNational.value },
        { id: 'events-national-row', allocations: eventsNational.value },
        { id: 'holidays-national-row', allocations: holidaysNational.value },
        {
            id: 'public-holidays-and-holidays-international-row',
            allocations: publicHolidaysAndHolidaysInternational.value,
        },
        { id: 'events-international-row', allocations: eventsInternational.value },
        { id: 'competition-national-and-international-row', allocations: competitionNationalAndInternational.value },
        { id: 'world-national-and-international-row', allocations: worldNationalAndInternational.value },
    ]);

    // Layout rows
    const layoutRows = computed(() =>
        rowData.value.map((row) => ({
            rowId: row.id,
            isFirstRow: false,
            layoutAlgorithm: 'Compact' as const,
            createButtonStatus: { type: 'Hidden' as const },
            allocations: [...row.allocations].sort((a, b) => a.startDateTime.toMillis() - b.startDateTime.toMillis()),
        })),
    );

    // for now calculate both allocation counts with the same length because the ExternalEvent timeline AND
    // the external Event aggregation rows in other timelines use the same first column component
    // and in the external event timeline (useExternalEventAllocations.ts) the X/Y information is needed
    const firstColumnCells = computed(() => {
        return rowData.value.map((row) => {
            return {
                rowId: row.id,
                allocationCount: {
                    inRange: row.allocations.length,
                    total: row.allocations.length,
                },
                label: $t('external-event-aggregations-first-row-label', { rowId: row.id }),
                variant: 'Condensed' as const,
            };
        });
    });

    return {
        layoutRows,
        firstColumnCells,
    };
}
